import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { RefreshCw, CheckCircle2, AlertTriangle, XCircle, Activity } from "lucide-react";

interface CheckEntry {
  status: "operational" | "degraded" | "down";
  latency_ms: number;
}

interface HealthResponse {
  status: "healthy" | "degraded" | "unhealthy";
  checks: Record<string, CheckEntry>;
  timestamp: string;
}

const statusConfig = {
  operational: { icon: CheckCircle2, color: "text-success", badge: "default" as const, label: "OK" },
  degraded: { icon: AlertTriangle, color: "text-warning", badge: "secondary" as const, label: "Aviso" },
  down: { icon: XCircle, color: "text-destructive", badge: "destructive" as const, label: "Erro" },
};

const checkLabels: Record<string, string> = {
  db: "Banco de Dados",
  auth: "Autenticação",
  edge: "Edge Functions",
  evolution_api: "Evolution API (WhatsApp)",
  lovable_gateway: "Gateway de IA",
  stripe: "Stripe (Pagamentos)",
};

export default function Health() {
  const [refetchKey, setRefetchKey] = useState(0);

  const { data, isLoading, isFetching } = useQuery<HealthResponse>({
    queryKey: ["health-check", refetchKey],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("health-check");
      if (error) throw error;
      return data as HealthResponse;
    },
    staleTime: 0,
  });

  const overallColor = data?.status === "healthy" ? "text-success"
    : data?.status === "degraded" ? "text-warning"
    : "text-destructive";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Activity className="w-6 h-6" /> Health Check
          </h1>
          <p className="text-muted-foreground">Status da infraestrutura do sistema</p>
        </div>
        <Button variant="outline" onClick={() => setRefetchKey((k) => k + 1)} disabled={isFetching}>
          <RefreshCw className={`w-4 h-4 mr-2 ${isFetching ? "animate-spin" : ""}`} />
          Verificar
        </Button>
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Verificando infraestrutura...</p>
      ) : data ? (
        <>
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Status Geral</CardTitle>
                <span className={`text-2xl font-bold ${overallColor}`}>
                  {data.status === "healthy" ? "✅ Saudável" : data.status === "degraded" ? "⚠️ Degradado" : "❌ Problemas"}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                Última verificação: {new Date(data.timestamp).toLocaleString("pt-BR")}
              </p>
            </CardHeader>
          </Card>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {Object.entries(data.checks).map(([name, check]) => {
              const cfg = statusConfig[check.status] || statusConfig.down;
              const Icon = cfg.icon;
              return (
                <Card key={name} className="hover:shadow-lg hover:scale-[1.02] hover:-translate-y-0.5 transition-all duration-300 ease-out">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-3">
                      <Icon className={`w-5 h-5 mt-0.5 ${cfg.color}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-sm">{checkLabels[name] || name}</span>
                          <Badge variant={cfg.badge} className="text-xs">{cfg.label}</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground break-words">{check.latency_ms}ms</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      ) : (
        <p className="text-muted-foreground">Nenhum dado disponível.</p>
      )}
    </div>
  );
}
